import React from 'react';

/**
 * Detects the operating system and device type
 */
function detectOS() {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  const platform = navigator.platform || '';
  
  // Check for mobile devices first
  if (/android/i.test(userAgent)) {
    return 'android';
  }
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return 'iphone';
  }
  
  // Check for desktop OS
  if (/Win/i.test(platform) || /Win/i.test(userAgent)) {
    return 'windows';
  }
  if (/Mac/i.test(platform) || /Mac/i.test(userAgent)) {
    return 'mac';
  }
  if (/Linux/i.test(platform)) {
    return 'linux';
  }
  
  // Default fallback
  return 'unknown';
}

/**
 * Gets OS-specific instructions for enabling microphone permissions
 */
function getInstructions(os) {
  const instructions = {
    windows: {
      title: 'Enable Microphone on Windows',
      steps: [
        'Click the lock icon (🔒) or camera icon in your browser\'s address bar',
        'Find "Microphone" in the permissions list',
        'Change it from "Block" to "Allow"',
        'Refresh this page',
        'Alternatively, go to Windows Settings > Privacy > Microphone and ensure microphone access is enabled for your browser'
      ]
    },
    mac: {
      title: 'Enable Microphone on Mac',
      steps: [
        'Click the lock icon (🔒) or camera icon in your browser\'s address bar',
        'Find "Microphone" in the permissions list',
        'Change it from "Block" to "Allow"',
        'Refresh this page',
        'Alternatively, go to System Preferences > Security & Privacy > Privacy > Microphone and ensure your browser is checked'
      ]
    },
    android: {
      title: 'Enable Microphone on Android',
      steps: [
        'Tap the three dots (⋮) menu in your browser',
        'Go to Settings > Site Settings > Microphone',
        'Ensure this site is allowed to use the microphone',
        'If needed, go to Android Settings > Apps > [Your Browser] > Permissions > Microphone and enable it',
        'Refresh this page'
      ]
    },
    iphone: {
      title: 'Enable Microphone on iPhone/iPad',
      steps: [
        'Tap the "aA" icon in the Safari address bar',
        'Select "Website Settings"',
        'Enable "Microphone"',
        'Refresh this page',
        'If needed, go to iPhone Settings > Safari > Microphone and ensure it\'s enabled'
      ]
    },
    linux: {
      title: 'Enable Microphone on Linux',
      steps: [
        'Click the lock icon (🔒) or camera icon in your browser\'s address bar',
        'Find "Microphone" in the permissions list',
        'Change it from "Block" to "Allow"',
        'Refresh this page'
      ]
    },
    unknown: {
      title: 'Enable Microphone Access',
      steps: [
        'Click the lock icon (🔒) or camera icon in your browser\'s address bar',
        'Find "Microphone" in the permissions list',
        'Change it from "Block" to "Allow"',
        'Refresh this page',
        'Check your device\'s system settings for microphone permissions'
      ]
    }
  };
  
  return instructions[os] || instructions.unknown;
}

function MicrophonePermissionPopup({ isOpen, onClose }) {
  if (!isOpen) return null;
  
  const os = detectOS();
  const instructions = getInstructions(os);
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-800">{instructions.title}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-full p-1"
            aria-label="Close"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6">
          <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-yellow-800 font-semibold">
              ⚠️ Microphone access has been denied. Please follow these steps to enable it:
            </p>
          </div>
          
          <ol className="list-decimal list-inside space-y-3 text-gray-700">
            {instructions.steps.map((step, index) => (
              <li key={index} className="pl-2">
                {step}
              </li>
            ))}
          </ol>
          
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-blue-800 text-sm">
              <strong>Note:</strong> After enabling microphone permissions, please refresh this page for the changes to take effect.
            </p>
          </div>
        </div>
        
        {/* Footer */}
        <div className="flex justify-end p-6 border-t bg-gray-50">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            Got it, I'll enable it
          </button>
        </div>
      </div>
    </div>
  );
}

export default MicrophonePermissionPopup;

